﻿'use strict';

var OktaAuth = require('@okta/okta-auth-js');

var queryString = require('query-string'); // Convert JSON to URL-encoded string for token endpoint
var uuidV4 = require('uuid/v4'); // UUIDs for CSRF tokens

/**
* Factory for handling authentication and items around authentication
* @memberOf components.authenticationModule
* @namespace authenticationFactory
*/

authenticationFactory.$inject = ['Restangular', '$q', '$cookies', 'data_project_constants', 'envService'];

function authenticationFactory(Restangular, $q, $cookies, data_project_constants, envService) {
    var service = {};

    var authenticationCookiesDomain = envService.read('auth_cookies_domain');

    /* Okta Identity Authentication */
    service.oktaAuthClient = new OktaAuth({
        issuer: envService.read(data_project_constants.oktaConfig.issuerUrl),
        clientId: envService.read(data_project_constants.oktaConfig.clientId),
        redirectUri: envService.read(data_project_constants.oktaConfig.redirectUrl),
        pkce: true,
        tokenManager: {
            storage: 'localStorage',
            secure: true,
            storageKey: data_project_constants.cookies.okta_storage_key
        }
    });

    service.shouldHandleOktaCallback = service.oktaAuthClient.token.isLoginRedirect();

    service.authenticateWithOkta = function () {
        service.oktaAuthClient.token.getWithRedirect({
            responseType: ['token', 'id_token'],
            scopes: ['openid']
        })
            .catch(function (error) {
                console.error("Failed to redirect to Okta login: " + error);
            });
    };

    service.parseOktaTokens = function () {
        return service.oktaAuthClient.token.parseFromUrl();
    };

    // Retrieves and parson JSON string for idToken and access token written to local storage by OktaAuth client
    service.getOktaAuthenticationObject = function () {
        var oktaAuthStr = localStorage.getItem(data_project_constants.cookies.okta_storage_key);
        if (!oktaAuthStr) return;

        var oktaAuthObject = JSON.parse(oktaAuthStr);

        return {
            idToken: oktaAuthObject.idToken,
            accessToken: oktaAuthObject.accessToken
        }
    }

    /* Legacy Identity Server Authentication */
    var authConnection = Restangular.withConfig(function (config) {
        config.setBaseUrl(envService.read('api_authentication_endpoint'));
        config.setDefaultHeaders({
            'Accept': 'application/x-www-form-urlencoded',
            'Content-Type': 'application/x-www-form-urlencoded'
        });

        // Restangular adds stuff to response objects, which we don't need for tokens.
        config.setPlainByDefault(true);
    });

    service.privateGetToken = function (postObject) {
        postObject = Object.assign(postObject, {
            client_id: 'dataportal',
            client_secret: 'dataportal'
        });

        return authConnection
            .all('token')
            .customPOST(queryString.stringify(postObject))
    };

    /**
    * Authenticate user
    * @method authenticate
    * @param {String} userName user name to check
    * @param {String} password password to check
    * @returns {Boolean} true if authenticated false if not
    * @memberOf components.authenticationModule.authenticationFactory
    */
    service.authenticate = function (userName, password) {
        var postObject = {
            grant_type: 'password',
            scope: 'offline_access dp_data use_csrf',
            username: userName,
            password: password
        };

        return service.privateGetToken(postObject).then(function (authObject) {
            $cookies.put(data_project_constants.cookies.csrf_name, uuidV4(), { secure: true, domain: authenticationCookiesDomain });
            service.setAuthenticationObject(authObject);
        });
    }

    /**
    * Refresh the authentication object
    * @method refreshAuthentication
    * @returns {Boolean} True if authentication object is refreshed, false if not
    * @memberOf components.authenticationModule.authenticationFactory
    */
    service.refreshAuthentication = function () {
        var auth = service.getAuthenticationObject();

        if (!auth) return $q.reject(false);

        var postObject = {
            grant_type: 'refresh_token',
            refresh_token: auth.refreshToken
        };

        return service.privateGetToken(postObject).then(function (authObject) {
            service.setAuthenticationObject(authObject);
        });
    }

    /**
    * Clear the authentication object
    * @method clearAuthentication
    * @returns {void}
    * @memberOf components.authenticationModule.authenticationFactory
    */
    service.clearAuthentication = function () {
        $cookies.remove(data_project_constants.cookies.csrf_name, { secure: true, domain: authenticationCookiesDomain });
        $cookies.remove(data_project_constants.cookies.user_name, { secure: true, domain: authenticationCookiesDomain });
    }

    /**
    * Sets the authentication object with the authentication and refresh values
    * @method setAuthenticationObject
    * @param {Object} authObject that will be used to refresh tokens and such
    * @memberOf components.authenticationModule.authenticationFactory
    */
    service.setAuthenticationObject = function (authObject) {
        $cookies.putObject(data_project_constants.cookies.user_name, authObject, { secure: true, domain: authenticationCookiesDomain });
    }

    /**
    * Gets the authentication object with the authentication and refresh values
    * @method getAuthenticationObject
    * @returns {Object} AuthenticationObject that will be used to refresh tokens and such
    * @memberOf components.authenticationModule.authenticationFactory
    */
    service.getAuthenticationObject = function () {
        var authCookie = $cookies.getObject(data_project_constants.cookies.user_name);
        if (!authCookie) return;

        return {
            refreshToken: authCookie.refresh_token,
            accessToken: authCookie.access_token
        };
    }

    /**
    * Checks if a browser is capable of using the library we use for generating unique strings for CSRF
    * @method isBrowserCompatible
    * @param {Object} globalObj the object to test for compatibility
    * @returns {Boolean} True if the browser is compatible with our app, false if not
    * @memberOf components.authenticationModule.authenticationFactory
    */
    service.isBrowserCompatible = function (globalObj) {
        var crypto = globalObj.crypto || globalObj.msCrypto;
        return !!(crypto && crypto.getRandomValues);
    }

    return service;
}

module.exports = authenticationFactory;



//////////////////
// WEBPACK FOOTER
// ./source/angular/components/authentication/authenticationFactory.js
// module id = 8
// module chunks = 0